import { notification } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';

import { ieLeasingApi } from '@/components/App/Leasing/api';
import type { TLeasingSubject } from '@/components/App/Leasing/types';
import {
  Button,
  FileDownload,
  FileUpload,
  Input
} from '@/components/Shared/ui';
import { isValidUrl } from '@/services';
import { formatPrice } from '@/services/formatter';
import { handleError } from '@/services/handleError';
import { useAppDispatch, useAppSelector } from '@/store';
import type { FileType } from '@/types/uploadFiles';

import { IeLeasingOrderContext } from '../../../IeOrder';
import { useSelectedOffer } from '../../../hooks';
import { refreshOrderData } from '../../../store';

type TLeasingContractVehicle = {
  subjectId: string;
  vinCodes: string[];
};

type TLeasingContractForm = {
  contractNumber: string;
  invoiceFile: FileType | undefined;
  salesDocumentSigningLink: string;
  vehicles: TLeasingContractVehicle[];
};

const CurrentLeasingView: React.FC = () => {
  const dispatch = useAppDispatch();

  const { isActionsDisabled } = useContext(IeLeasingOrderContext);

  const { dicts } = useAppSelector((state) => state.dict);
  const { request } = useAppSelector((state) => state.ieLeasingOrder);

  const leasingSubjects = request?.leasingSubjects ?? [];

  const vehiclesCount = leasingSubjects.reduce(
    (sum, subject) => sum + (subject.quantity || 0),
    0
  );

  const { selectedOffer } = useSelectedOffer();

  const [throttleEnabled, setThrottleEnabled] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isFieldsFrozen, setIsFieldsFrozen] = useState(isActionsDisabled);

  const { control, getValues, handleSubmit, formState, reset, trigger } =
    useForm<TLeasingContractForm>({
      mode: 'all',
      delayError: 200,
      defaultValues: {
        contractNumber: '',
        salesDocumentSigningLink: '',
        vehicles: []
      }
    });

  const isSubmitDisabled =
    throttleEnabled ||
    Object.values(formState.errors).length > 0 ||
    !selectedOffer;

  const sendDocumentsHandler: SubmitHandler<TLeasingContractForm> = async (
    data
  ) => {
    if (!selectedOffer || !dicts || isActionsDisabled) return;

    setIsSubmitLoading(true);
    try {
      if (!data.invoiceFile?.base64) {
        notification.warning({
          message: 'Необходимо загрузить файл с счетом на оплату'
        });
        return;
      }

      const vehicles = leasingSubjects.map((subject, index) => ({
        subjectId: subject.uuid,
        vinCodes: (data.vehicles[index]?.vinCodes ?? []).map((vinCode) =>
          vinCode.trim()
        )
      }));

      const hasInvalidVehicles = vehicles.some(
        (vehicle, index) =>
          vehicle.vinCodes.length !== leasingSubjects[index].quantity ||
          vehicle.vinCodes.some((vinCode) => !vinCode)
      );

      if (hasInvalidVehicles) {
        notification.warning({
          message: 'Укажите VIN-код для каждого автомобиля'
        });
        return;
      }

      await ieLeasingApi.sendLeasingContractData(selectedOffer.uuid, {
        contractNumber: data.contractNumber,
        invoiceFile: data.invoiceFile.base64,
        salesDocumentSigningLink: data.salesDocumentSigningLink,
        vehicles
      });

      notification.success({
        title: 'Успешно',
        description: 'Данные были успешно отправлены',
        duration: 5
      });

      dispatch(refreshOrderData());

      setThrottleEnabled(true);
      setTimeout(() => setThrottleEnabled(false), 10000);
    } catch (error) {
      handleError(error, {
        customTitle: 'Ошибка при отправке данных',
        customDescription: 'Пожалуйста попробуйте позже'
      });
    } finally {
      setIsSubmitLoading(false);
    }
  };

  useEffect(() => {
    const {
      invoiceLink,
      purchaseContractNumber,
      salesDocumentSigningLink,
      status
    } = selectedOffer || {};

    setIsFieldsFrozen(
      isActionsDisabled ||
        status?.code === 'financed' ||
        !!invoiceLink ||
        !!purchaseContractNumber ||
        !!salesDocumentSigningLink
    );

    if (!selectedOffer) {
      return;
    }

    reset({
      contractNumber: purchaseContractNumber || '',
      salesDocumentSigningLink: salesDocumentSigningLink || '',
      vehicles: buildVehicles(leasingSubjects)
    });
  }, [leasingSubjects, selectedOffer, isActionsDisabled]);

  return (
    <form
      className="ie-financing-step__form"
      onSubmit={handleSubmit(sendDocumentsHandler)}
    >
      <div className="ie-financing-step__field">
        <Controller
          control={control}
          name="contractNumber"
          rules={{
            required: 'Пожалуйста, введите номер контракта',
            validate: (value) => {
              if (!value) return 'Пожалуйста, введите номер контракта';
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label="Номер ДКП"
              error={error?.message}
              isRequired
              isFrozen={isFieldsFrozen}
              onChange={(e) => {
                // Допустимые знаки: любые буквы, цифры, тире, подчёркивание
                field.onChange(
                  e.target.value.replace(/[^a-zA-Zа-яА-ЯёЁ0-9\-_.\s]/g, '')
                );
              }}
            />
          )}
        />
      </div>

      <div className="ie-financing-step__field">
        <Controller
          control={control}
          name="salesDocumentSigningLink"
          rules={{
            required: 'Пожалуйста, введите ссылку',
            validate: (value) => {
              if (!value) return 'Пожалуйста, введите ссылку';
              if (!isValidUrl(value)) {
                return 'Некорректная ссылка';
              }
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              type="url"
              label="Ссылка на подписание 4-х стороннего ДКП"
              error={error?.message}
              isRequired
              isFrozen={isFieldsFrozen}
            />
          )}
        />
      </div>

      {leasingSubjects.length > 0 && (
        <div className="ie-financing-step__vins">
          <p className="ie-financing-step__vins-title">
            VIN автомобилей ({vehiclesCount} шт.)
          </p>

          {leasingSubjects.map((subject, subjectIndex) => (
            <div
              key={subject.uuid || subjectIndex}
              className="ie-financing-step__vin-group"
            >
              <div className="ie-financing-step__vin-group-header">
                <p className="ie-financing-step__vin-group-title">
                  {getSubjectTitle(subject)}
                </p>
                <p className="ie-financing-step__vin-group-meta">
                  {subject.quantity} × {formatPrice(subject.cost || 0)} ₸
                </p>
              </div>

              <div className="ie-financing-step__vin-grid">
                {Array.from({ length: subject.quantity }, (_, vinIndex) => (
                  <Controller
                    key={`${subject.uuid}-${vinIndex}`}
                    control={control}
                    name={`vehicles.${subjectIndex}.vinCodes.${vinIndex}`}
                    rules={{
                      required: 'Пожалуйста, введите данные',
                      minLength: {
                        value: 17,
                        message: 'VIN код должен состоять из 17 символов'
                      },
                      validate: (value) => validateVinCode(value, getValues)
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        {...field}
                        label={`VIN ${vinIndex + 1}`}
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(
                            /[^A-Za-z0-9]/g,
                            ''
                          );
                          if (value.length > 17) return;
                          field.onChange(value.toUpperCase());
                          getValues('vehicles').forEach((vehicle, sIndex) => {
                            vehicle.vinCodes?.forEach((vinCode, vIndex) => {
                              if (!vinCode) return;
                              void trigger(
                                `vehicles.${sIndex}.vinCodes.${vIndex}`
                              );
                            });
                          });
                        }}
                        error={error?.message}
                        isFrozen={isFieldsFrozen}
                        isRequired
                      />
                    )}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="ie-financing-step__field">
        {selectedOffer?.invoiceLink ? (
          <FileDownload
            fileLink={selectedOffer.invoiceLink}
            prevStateTitle="Счет на оплату"
            oldDownloadMethod
          />
        ) : (
          <Controller
            control={control}
            name="invoiceFile"
            rules={{
              validate: (value) => {
                if (!value?.base64) return 'Пожалуйста, выберите файл';
                if ('size' in value && (value?.size || 0) > 1 * 1024 * 1024)
                  return 'Файл не должен превышать 1 МБ';
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <FileUpload
                title="Счет на оплату"
                file={field.value}
                onChange={field.onChange}
                onDelete={() => field.onChange(undefined)}
                error={error?.message}
                isRequired
                accept=".pdf"
                allowedExtensions={['pdf']}
                isFrozen={isFieldsFrozen}
              />
            )}
          />
        )}
      </div>

      {!isFieldsFrozen && (
        <Button
          className="ie-financing-step__submit"
          variant="filled-red"
          disabled={isSubmitDisabled || isActionsDisabled}
          isLoading={isSubmitLoading}
        >
          Отправить документы
        </Button>
      )}
    </form>
  );
};

const buildVehicles = (
  subjects: TLeasingSubject[]
): TLeasingContractVehicle[] =>
  subjects.map((subject) => ({
    subjectId: subject.uuid,
    vinCodes: Array.from(
      { length: subject.quantity },
      (_, index) => subject.vinCodes?.[index] ?? ''
    )
  }));

const getSubjectTitle = (subject: TLeasingSubject): string => {
  const carName = [
    subject.brand?.name,
    subject.model?.name,
    subject.equipmentName
  ]
    .filter(Boolean)
    .join(' ');
  const year = subject.year ? String(subject.year) : '';

  return [carName, year].filter(Boolean).join(', ');
};

const validateVinCode = (
  value: string,
  getValues: () => TLeasingContractForm
): string | undefined => {
  if (/[IOQ]/.test(value)) {
    return 'В поле VIN не должно быть букв I, O, Q';
  }

  const duplicates = getValues()
    .vehicles.flatMap((vehicle) => vehicle.vinCodes ?? [])
    .filter((vinCode) => vinCode && vinCode === value);

  if (duplicates.length > 1) return 'Такой VIN уже указан';
};

export default CurrentLeasingView;
